import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { TranslationNamespace } from 'i18n';
import { MappingsConfig, StatusMappingConfig } from 'types';

import { BidirectionalMappingTable } from './BidirectionalMappingTable';
import { StatusMappingsEditor } from './StatusMappingsEditor';

type Props = {
  mappings: MappingsConfig | undefined;
  statusMappings: StatusMappingConfig | undefined;
  onMappingsChange: (value: MappingsConfig) => void;
  onStatusMappingsChange: (value: StatusMappingConfig) => void;
  disabled?: boolean;
};

const KEYS = {
  use_custom_base_mappings:
    'pages.dynamic_adapter_configs.dialog.full_mappings.use_custom_base_mappings',
  status_mappings_title:
    'pages.dynamic_adapter_configs.dialog.full_mappings.status_mappings_title',
  payment_types_title:
    'pages.dynamic_adapter_configs.dialog.full_mappings.payment_types_title',
  banks_title: 'pages.dynamic_adapter_configs.dialog.full_mappings.banks_title',
  payment_types_overrides:
    'pages.dynamic_adapter_configs.dialog.full_mappings.payment_types_overrides',
  banks_overrides:
    'pages.dynamic_adapter_configs.dialog.full_mappings.banks_overrides',
  inline_mappings_info:
    'pages.dynamic_adapter_configs.dialog.full_mappings.inline_mappings_info',
  overrides_info:
    'pages.dynamic_adapter_configs.dialog.full_mappings.overrides_info',
} as const;

const getDefaultMappings = (): MappingsConfig => ({
  source: 'appConfig',
});

export const FullMappingsEditor: React.FC<Props> = ({
  mappings,
  statusMappings,
  onMappingsChange,
  onStatusMappingsChange,
  disabled = false,
}) => {
  const { t: tAdmin } = useTranslation(TranslationNamespace.Admin);
  const t = tAdmin as unknown as (key: string) => string;

  const config = mappings || getDefaultMappings();

  const handleCustomMappingsToggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newSource = event.target.checked ? 'inline' : 'appConfig';
      onMappingsChange({ ...config, source: newSource });
    },
    [config, onMappingsChange],
  );

  const handlePaymentTypesChange = useCallback(
    (value: {
      toProvider: Record<string, string>;
      toInternal: Record<string, string>;
    }) => {
      onMappingsChange({ ...config, paymentTypes: value });
    },
    [config, onMappingsChange],
  );

  const handleBanksChange = useCallback(
    (value: {
      toProvider: Record<string, string>;
      toInternal: Record<string, string>;
    }) => {
      onMappingsChange({ ...config, banks: value });
    },
    [config, onMappingsChange],
  );

  const handlePaymentTypesOverridesChange = useCallback(
    (value: {
      toProvider: Record<string, string>;
      toInternal: Record<string, string>;
    }) => {
      onMappingsChange({ ...config, paymentTypesOverrides: value });
    },
    [config, onMappingsChange],
  );

  const handleBanksOverridesChange = useCallback(
    (value: {
      toProvider: Record<string, string>;
      toInternal: Record<string, string>;
    }) => {
      onMappingsChange({ ...config, banksOverrides: value });
    },
    [config, onMappingsChange],
  );

  return (
    <Stack spacing={4}>
      {/* Status Mappings Section */}
      <Box>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          {t(KEYS.status_mappings_title)}
        </Typography>
        <StatusMappingsEditor
          value={statusMappings}
          onChange={onStatusMappingsChange}
          disabled={disabled}
        />
      </Box>

      <Divider />

      {/* Custom Base Mappings Toggle */}
      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={config.source === 'inline'}
              onChange={handleCustomMappingsToggle}
              disabled={disabled}
            />
          }
          label={t(KEYS.use_custom_base_mappings)}
        />
      </Box>

      {/* Inline Mappings (only visible when custom mappings enabled) */}
      {config.source === 'inline' && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t(KEYS.inline_mappings_info)}
            </Typography>

            <Stack spacing={3}>
              <Box>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: 500 }}
                >
                  {t(KEYS.payment_types_title)}
                </Typography>
                <BidirectionalMappingTable
                  value={config.paymentTypes}
                  onChange={handlePaymentTypesChange}
                  disabled={disabled}
                />
              </Box>

              <Box>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: 500 }}
                >
                  {t(KEYS.banks_title)}
                </Typography>
                <BidirectionalMappingTable
                  value={config.banks}
                  onChange={handleBanksChange}
                  disabled={disabled}
                />
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Divider />

      {/* Overrides (always visible) */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t(KEYS.overrides_info)}
          </Typography>

          <Stack spacing={3}>
            <Box>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{ fontWeight: 500 }}
              >
                {t(KEYS.payment_types_overrides)}
              </Typography>
              <BidirectionalMappingTable
                value={config.paymentTypesOverrides}
                onChange={handlePaymentTypesOverridesChange}
                disabled={disabled}
              />
            </Box>

            <Box>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{ fontWeight: 500 }}
              >
                {t(KEYS.banks_overrides)}
              </Typography>
              <BidirectionalMappingTable
                value={config.banksOverrides}
                onChange={handleBanksOverridesChange}
                disabled={disabled}
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};
